import type { AnswerT } from '../../../../common/types/types';
import type { DynamicAnswerSpanT, DynamicAnswerTracker, SpanT } from '../types';
export function areDynamicAnswersEqual(a: AnswerT, b: AnswerT): boolean {
  const aKeys = Object.keys(a);
  const bKeySet = new Set(Object.keys(b));
  if (aKeys.length !== bKeySet.size) {
    return false;
  }
  for (const key of aKeys) {
    if (!bKeySet.has(key)) {
      return false;
    }
    const aEntry = a[key];
    const bEntry = b[key];
    if (!aEntry || !bEntry) {
      return false;
    }

    // Compare string answers only (unordered) — File entries cannot be meaningfully compared.
    const aAnswers = aEntry.answers.filter((ans): ans is string => typeof ans === 'string').sort();
    const bAnswers = bEntry.answers.filter((ans): ans is string => typeof ans === 'string').sort();
    if (aAnswers.length !== bAnswers.length) {
      return false;
    }
    for (let j = 0; j < aAnswers.length; j++) {
      if (aAnswers[j] !== bAnswers[j]) {
        return false;
      }
    }
  }
  return true;
}
function hasDynamicAnswers(answers: AnswerT): boolean {
  return Object.keys(answers).length > 0;
}

/**
 * Builds per-annotation dynamic answer segments by recording frame-by-frame observations.
 * A new segment is started whenever the answers change or there is a gap in frame coverage.
 * call `build()` after all frames are processed to close any open segment (including
 * single-frame segments) and return the final results.
 */
export class DynamicAnswerSegmentBuilder {
  private readonly trackers = new Map<string, DynamicAnswerTracker>();
  record(annotationId: string, frameIndex: number, dynamicAnswers: AnswerT): void {
    if (!hasDynamicAnswers(dynamicAnswers)) {
      return;
    }
    const tracker = this.trackers.get(annotationId);
    if (!tracker) {
      this.trackers.set(annotationId, {
        currentAnswers: dynamicAnswers,
        segmentStart: frameIndex,
        lastFrameIndex: frameIndex,
        segments: []
      });
      return;
    }
    const hasGap = frameIndex > tracker.lastFrameIndex + 1;
    const answersChanged = !areDynamicAnswersEqual(dynamicAnswers, tracker.currentAnswers);
    if (hasGap || answersChanged) {
      if (hasDynamicAnswers(tracker.currentAnswers)) {
        tracker.segments.push({
          frameSpan: {
            start: tracker.segmentStart,
            end: tracker.lastFrameIndex
          },
          answers: tracker.currentAnswers
        });
      }
      tracker.currentAnswers = dynamicAnswers;
      tracker.segmentStart = frameIndex;
    }
    tracker.lastFrameIndex = frameIndex;
  }
  build(): Map<string, DynamicAnswerSpanT[]> {
    const result = new Map<string, DynamicAnswerSpanT[]>();
    for (const [annotationId, tracker] of this.trackers) {
      if (hasDynamicAnswers(tracker.currentAnswers)) {
        tracker.segments.push({
          frameSpan: {
            start: tracker.segmentStart,
            end: tracker.lastFrameIndex
          },
          answers: tracker.currentAnswers
        });
      }
      if (tracker.segments.length > 0) {
        result.set(annotationId, tracker.segments);
      }
    }
    return result;
  }
}

/**
 * Merges neighbors that carry the same value and touch (no frame gap) into one segment, so
 * adjacent segments never repeat a value. Input must be sorted by frameSpan.start.
 */
function coalesceAdjacentEqualSegments(segments: DynamicAnswerSpanT[]): DynamicAnswerSpanT[] {
  return segments.reduce<DynamicAnswerSpanT[]>((merged, segment) => {
    const lastDynamicAnswerSpan = merged[merged.length - 1];
    if (lastDynamicAnswerSpan && segment.frameSpan.start <= lastDynamicAnswerSpan.frameSpan.end + 1 && areDynamicAnswersEqual(segment.answers, lastDynamicAnswerSpan.answers)) {
      const combined: DynamicAnswerSpanT = {
        frameSpan: {
          start: lastDynamicAnswerSpan.frameSpan.start,
          end: Math.max(lastDynamicAnswerSpan.frameSpan.end, segment.frameSpan.end)
        },
        answers: lastDynamicAnswerSpan.answers
      };
      return [...merged.slice(0, -1), combined];
    }
    return [...merged, segment];
  }, []);
}

/**
 * Inserts or updates a dynamic answer segment into the existing array.
 * Segments overlapping the new span are split: non-overlapping portions are preserved and the
 * overlapping portion is replaced. Adjacent segments left holding the same value are then coalesced
 * so extending/interpolating with an unchanged value grows one span instead of duplicating it.
 * Result is sorted by frameSpan.start.
 */
export function updateDynamicAnswerSegments(existing: DynamicAnswerSpanT[], update: {
  frameSpan: SpanT;
  answers: AnswerT;
}): DynamicAnswerSpanT[] {
  const result: DynamicAnswerSpanT[] = [];
  for (const segment of existing) {
    const noOverlap = segment.frameSpan.end < update.frameSpan.start || segment.frameSpan.start > update.frameSpan.end;
    if (noOverlap) {
      result.push(segment);
    } else {
      if (segment.frameSpan.start < update.frameSpan.start) {
        result.push({
          frameSpan: {
            start: segment.frameSpan.start,
            end: update.frameSpan.start - 1
          },
          answers: segment.answers
        });
      }
      if (segment.frameSpan.end > update.frameSpan.end) {
        result.push({
          frameSpan: {
            start: update.frameSpan.end + 1,
            end: segment.frameSpan.end
          },
          answers: segment.answers
        });
      }
    }
  }
  result.push({
    frameSpan: update.frameSpan,
    answers: update.answers
  });
  result.sort((a, b) => a.frameSpan.start - b.frameSpan.start);
  return coalesceAdjacentEqualSegments(result);
}
export function removeAnswerForQuestion(answers: AnswerT | undefined, questionId: string): AnswerT | undefined {
  if (!answers || !(questionId in answers)) {
    return answers;
  }
  const remaining = {
    ...answers
  };
  delete remaining[questionId];
  return remaining;
}
function overlaps(a: SpanT, b: SpanT): boolean {
  return a.start <= b.end && a.end >= b.start;
}
export function removeQuestionFromDynamicAnswerSegments(segments: DynamicAnswerSpanT[], questionId: string, removeSpan: SpanT): DynamicAnswerSpanT[] {
  const result: DynamicAnswerSpanT[] = [];
  for (const segment of segments) {
    const {
      start,
      end
    } = segment.frameSpan;
    if (!overlaps(segment.frameSpan, removeSpan) || !(questionId in segment.answers)) {
      result.push(segment);
      continue;
    }
    if (start < removeSpan.start) {
      result.push({
        frameSpan: {
          start,
          end: removeSpan.start - 1
        },
        answers: segment.answers
      });
    }
    if (end > removeSpan.end) {
      result.push({
        frameSpan: {
          start: removeSpan.end + 1,
          end
        },
        answers: segment.answers
      });
    }
    const remainingAnswers = removeAnswerForQuestion(segment.answers, questionId);
    if (remainingAnswers && Object.keys(remainingAnswers).length > 0) {
      result.push({
        frameSpan: {
          start: Math.max(start, removeSpan.start),
          end: Math.min(end, removeSpan.end)
        },
        answers: remainingAnswers
      });
    }
  }
  result.sort((a, b) => a.frameSpan.start - b.frameSpan.start);
  return coalesceAdjacentEqualSegments(result);
}
export function setQuestionAnswerInSegments(segments: DynamicAnswerSpanT[], questionId: string, answer: AnswerT[string], span: SpanT): DynamicAnswerSpanT[] {
  const result: DynamicAnswerSpanT[] = [];
  for (const segment of segments) {
    const {
      start,
      end
    } = segment.frameSpan;
    if (!overlaps(segment.frameSpan, span)) {
      result.push(segment);
      continue;
    }
    if (start < span.start) {
      result.push({
        frameSpan: {
          start,
          end: span.start - 1
        },
        answers: segment.answers
      });
    }
    if (end > span.end) {
      result.push({
        frameSpan: {
          start: span.end + 1,
          end
        },
        answers: segment.answers
      });
    }
    result.push({
      frameSpan: {
        start: Math.max(start, span.start),
        end: Math.min(end, span.end)
      },
      answers: {
        ...segment.answers,
        [questionId]: answer
      }
    });
  }
  result.sort((a, b) => a.frameSpan.start - b.frameSpan.start);
  return coalesceAdjacentEqualSegments(result);
}

/**
 * Removes dynamic answer segments overlapping the given span.
 * Non-overlapping portions of partially-overlapping segments are preserved.
 * Result is sorted by frameSpan.start.
 */
export function removeDynamicAnswerSegments(existing: DynamicAnswerSpanT[], removeSpan: SpanT): DynamicAnswerSpanT[] {
  const result: DynamicAnswerSpanT[] = [];
  for (const segment of existing) {
    const noOverlap = segment.frameSpan.end < removeSpan.start || segment.frameSpan.start > removeSpan.end;
    if (noOverlap) {
      result.push(segment);
    } else {
      if (segment.frameSpan.start < removeSpan.start) {
        result.push({
          frameSpan: {
            start: segment.frameSpan.start,
            end: removeSpan.start - 1
          },
          answers: segment.answers
        });
      }
      if (segment.frameSpan.end > removeSpan.end) {
        result.push({
          frameSpan: {
            start: removeSpan.end + 1,
            end: segment.frameSpan.end
          },
          answers: segment.answers
        });
      }
    }
  }
  result.sort((a, b) => a.frameSpan.start - b.frameSpan.start);
  return result;
}